import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);

const ACTIVE_USER_KEY = "wc.activeUserId";
const UNLOCK_KEY = "wc.lastUnlockAt";
const UNLOCK_WINDOW_MS = 24 * 60 * 60 * 1000;

export function AppProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null);
  const [members, setMembers] = useState([]);
  const [catches, setCatches] = useState([]);
  const [activeUserId, setActiveUserIdState] = useState(null);
  const [unlocked, setUnlocked] = useState(false);

  const setActiveUserId = useCallback((id) => {
    setActiveUserIdState(id);
    if (id) localStorage.setItem(ACTIVE_USER_KEY, id);
  }, []);

  const markUnlocked = useCallback(() => {
    localStorage.setItem(UNLOCK_KEY, Date.now().toString());
    setUnlocked(true);
  }, []);

  const lock = useCallback(() => {
    localStorage.removeItem(UNLOCK_KEY);
    setUnlocked(false);
  }, []);

  const refresh = useCallback(async () => {
    const [fam, cs] = await Promise.all([api.getFamily(), api.listCatches().catch(() => [])]);
    if (!fam.exists) {
      setFamily(null);
      setMembers([]);
      setCatches([]);
      return { exists: false };
    }
    setFamily(fam.family);
    setMembers(fam.members);
    setCatches(cs);
    return { exists: true, family: fam.family, members: fam.members, catches: cs };
  }, []);

  // First load
  useEffect(() => {
    (async () => {
      try {
        await refresh();
        // Restore unlock
        const last = parseInt(localStorage.getItem(UNLOCK_KEY) || "0", 10);
        if (last && Date.now() - last < UNLOCK_WINDOW_MS) setUnlocked(true);
        // Restore active user
        const storedId = localStorage.getItem(ACTIVE_USER_KEY);
        if (storedId) setActiveUserIdState(storedId);
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  // If active user was deleted or never picked, default to first member
  useEffect(() => {
    if (!members.length) return;
    if (!activeUserId || !members.find((m) => m.id === activeUserId)) {
      setActiveUserId(members[0].id);
    }
  }, [members, activeUserId, setActiveUserId]);

  const activeUser = useMemo(
    () => members.find((m) => m.id === activeUserId) || null,
    [members, activeUserId],
  );

  const value = {
    loading,
    family,
    members,
    catches,
    activeUser,
    activeUserId,
    setActiveUserId,
    unlocked,
    markUnlocked,
    lock,
    refresh,
    setFamily,
    setMembers,
    setCatches,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
